/**
 * Size Chart — Little Giants PK / Fabric Theme
 *
 * Two custom elements:
 *   <size-chart-modal>  — the dialog itself
 *   <size-chart-button> — the trigger wrapper
 *
 * Unit switching:
 *   Pill buttons (.size-chart-unit-pill[data-unit="in|cm"]) drive the toggle.
 *   Switchable cells carry data-inches and data-cm attributes; JS swaps textContent.
 *
 * Transposing:
 *   Charts are authored in snippets/size-chart.liquid the tall way — one row
 *   per size, one column per measurement. With 18 sizes that is a long
 *   vertical scroll on a phone. _transposeTables() flips each table once so
 *   measurements run down the first column and sizes run across the top,
 *   which turns the scroll sideways and keeps the sheet short.
 *
 *   It MOVES the original cell nodes rather than copying their text, so the
 *   .unit-switchable class and the data-inches / data-cm attributes travel
 *   with each cell and _switchUnit() keeps working untouched.
 */

class SizeChartModal extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.activeUnit = 'in';
    this.addEventListener('keyup', (e) => { if (e.key === 'Escape') this.close(); });
    this.addEventListener('click', this._handleClick.bind(this));
  }

  connectedCallback() {
    this._transposeTables();
    this._initScrollShadows();
  }

  /**
   * Re-tag a cell as th/td, carrying its attributes and child nodes across so
   * .unit-switchable and data-inches / data-cm survive the move.
   */
  _reTagCell(source, tagName, scope) {
    const cell = document.createElement(tagName);
    Array.from(source.attributes).forEach((attr) => {
      cell.setAttribute(attr.name, attr.value);
    });
    if (scope) cell.setAttribute('scope', scope);
    while (source.firstChild) cell.appendChild(source.firstChild);
    return cell;
  }

  /**
   * Flip each table from "one row per size" to "one row per measurement".
   * Idempotent — guarded by data-lg-transposed.
   */
  _transposeTables() {
    this.querySelectorAll('.size-chart-modal__table').forEach((table) => {
      if (table.dataset.lgTransposed === 'true') return;

      const headCells = Array.from(table.querySelectorAll('thead th'));
      const bodyRows = Array.from(table.querySelectorAll('tbody tr'));
      if (headCells.length < 2 || bodyRows.length < 1) return;

      // Merged cells would not survive a transpose — leave those charts alone.
      const hasSpans = Array.from(table.querySelectorAll('th, td')).some(
        (cell) => cell.colSpan > 1 || cell.rowSpan > 1
      );
      if (hasSpans) return;

      const grid = bodyRows.map((row) => Array.from(row.children));
      const isRectangular = grid.every((cells) => cells.length === headCells.length);
      if (!isRectangular) return;

      // New header row: the original corner label, then the original first
      // column (the size names) running across the top.
      const newThead = document.createElement('thead');
      const headRow = document.createElement('tr');
      headRow.appendChild(headCells[0]);
      grid.forEach((cells) => {
        headRow.appendChild(this._reTagCell(cells[0], 'th', 'col'));
      });
      newThead.appendChild(headRow);

      // One body row per remaining measurement column.
      const newTbody = document.createElement('tbody');
      for (let i = 1; i < headCells.length; i++) {
        const row = document.createElement('tr');
        row.appendChild(this._reTagCell(headCells[i], 'th', 'row'));
        grid.forEach((cells) => row.appendChild(cells[i]));
        newTbody.appendChild(row);
      }

      table.replaceChildren(newThead, newTbody);
      table.dataset.lgTransposed = 'true';
    });
  }

  _initScrollShadows() {
    this.querySelectorAll('.size-chart-modal__table-wrapper').forEach((wrapper) => {
      const update = () => {
        wrapper.classList.toggle('is-scrolled-y', wrapper.scrollTop > 2);
        wrapper.classList.toggle('is-scrolled-x', wrapper.scrollLeft > 2);
      };
      wrapper.addEventListener('scroll', update, { passive: true });
      update();
    });
  }

  _handleClick(event) {
    const target = event.target;

    if (target.closest('[data-action="close"]')) {
      this.close();
      return;
    }

    const pill = target.closest('.size-chart-unit-pill');
    if (pill) {
      event.stopPropagation();
      this._switchUnit(pill.getAttribute('data-unit'));
      return;
    }

    const guideBtn = target.closest('[data-action="toggle-guide"]');
    if (guideBtn) {
      event.stopPropagation();
      this._toggleGuide(guideBtn);
      return;
    }
  }

  _toggleGuide(btn) {
    const guide = this.querySelector('.size-chart-modal__guide');
    const tableWrapper = this.querySelector('.size-chart-modal__table-wrapper');
    if (!guide || !tableWrapper) return;

    const showingGuide = !guide.hidden;
    guide.hidden = showingGuide;
    tableWrapper.hidden = !showingGuide;
    btn.setAttribute('aria-expanded', String(!showingGuide));
    btn.textContent = showingGuide ? 'View Guide' : 'View Table';

    this.querySelectorAll('.size-chart-unit-pill').forEach((pill) => {
      pill.style.display = showingGuide ? '' : 'none';
    });
  }

  _switchUnit(unit) {
    if (unit === this.activeUnit) return;
    this.activeUnit = unit;

    this.querySelectorAll('.size-chart-unit-pill').forEach((btn) => {
      btn.classList.toggle('is-active', btn.getAttribute('data-unit') === unit);
    });

    const attr = unit === 'cm' ? 'data-cm' : 'data-inches';
    this.querySelectorAll('.unit-switchable').forEach((el) => {
      const val = el.getAttribute(attr);
      if (val !== null) el.textContent = val;
    });
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;
    this.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('size-chart-open');

    this._transposeTables();

    const defaultUnit = this.getAttribute('data-default-unit') || 'in';
    this._switchUnit(defaultUnit);

    const guide = this.querySelector('.size-chart-modal__guide');
    const tableWrapper = this.querySelector('.size-chart-modal__table-wrapper');
    const guideBtn = this.querySelector('[data-action="toggle-guide"]');
    if (guide) guide.hidden = true;
    if (tableWrapper) tableWrapper.hidden = false;
    if (guideBtn) { guideBtn.setAttribute('aria-expanded', 'false'); guideBtn.textContent = 'View Guide'; }
    this.querySelectorAll('.size-chart-unit-pill').forEach((pill) => { pill.style.display = ''; });

    this._initScrollShadows();

    this.dispatchEvent(new CustomEvent('size-chart:opened'));

    const firstFocusable = this.querySelector('button, [href], input, [tabindex]:not([tabindex="-1"])');
    if (firstFocusable) firstFocusable.focus();
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('size-chart-open');
    this.dispatchEvent(new CustomEvent('size-chart:closed'));

    if (this._returnFocus) {
      this._returnFocus.focus();
      this._returnFocus = null;
    }
  }
}

class SizeChartButton extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this._handleClick.bind(this));
  }

  _handleClick() {
    const targetId = this.getAttribute('data-target');
    const modal = document.getElementById(targetId);
    if (!modal) return;

    modal._returnFocus = this.querySelector('button') || this;
    const innerBtn = this.querySelector('[aria-expanded]');
    if (innerBtn) innerBtn.setAttribute('aria-expanded', 'true');

    modal.open();

    modal.addEventListener('size-chart:closed', () => {
      if (innerBtn) innerBtn.setAttribute('aria-expanded', 'false');
    }, { once: true });
  }
}

if (!customElements.get('size-chart-modal')) {
  customElements.define('size-chart-modal', SizeChartModal);
}
if (!customElements.get('size-chart-button')) {
  customElements.define('size-chart-button', SizeChartButton);
}
